import { Temporal } from "@js-temporal/polyfill";

/**
 * Format a date-only string (e.g. "2000-01-01" or "2000-01-01T00:00:00Z") as a PlainDate,
 * avoiding timezone offset issues that occur with JS Date.
 * Example: formatPlainDate("2000-01-01T00:00:00Z", "MM/DD/YYYY") -> "01/01/2000"
 */
export const formatPlainDate = (
	dateString: string | null | undefined,
	format: "MM/DD/YYYY" | "YYYY-MM-DD" | "DD/MM/YYYY" = "MM/DD/YYYY",
): string | null => {
	if (!dateString) return null;

	try {
		// Extract just the date portion (handles both "2000-01-01" and "2000-01-01T00:00:00Z")
		const isoDate = dateString.substring(0, 10);
		const plainDate = Temporal.PlainDate.from(isoDate);

		switch (format) {
			case "MM/DD/YYYY":
				return `${String(plainDate.month).padStart(2, "0")}/${String(plainDate.day).padStart(2, "0")}/${plainDate.year}`;
			case "DD/MM/YYYY":
				return `${String(plainDate.day).padStart(2, "0")}/${String(plainDate.month).padStart(2, "0")}/${plainDate.year}`;
			case "YYYY-MM-DD":
				return plainDate.toString();
			default:
				return plainDate.toString();
		}
	} catch {
		return null;
	}
};

/**
 * Display a date of birth string that may be in ISO format (legacy: "1993-07-02T06:00:00.000Z")
 * or already formatted (new: "07/02/1993" or "02/07/1993").
 * If ISO, converts to the target display format using Temporal.PlainDate.
 * If already formatted, returns as-is.
 */
export const displayDateOfBirth = (
	dateString: string | null | undefined,
	displayFormat: "MM/DD/YYYY" | "DD/MM/YYYY" | "YYYY-MM-DD" = "MM/DD/YYYY",
): string | null => {
	if (!dateString) return null;

	// SIMS-8473: the STRINGS "undefined" and "null" are never a date of birth, but they are
	// truthy, so without this they fall through to the passthrough below and the literal word
	// lands on a participant's profile. That is not hypothetical — the profile called
	// `formatDob(String(beneficiary.dateOfBirth))`, which produces exactly this for a missing
	// value. The call site is fixed too; this is the layer that makes the whole class harmless.
	const trimmed = dateString.trim();
	if (!trimmed || trimmed === "undefined" || trimmed === "null") return null;

	// Check if it looks like an ISO date (starts with YYYY-MM-DD pattern)
	const isoPattern = /^\d{4}-\d{2}-\d{2}/;
	if (isoPattern.test(dateString)) {
		return formatPlainDate(dateString, displayFormat);
	}

	// Already in a display format (e.g., "07/02/1993" or "02/07/1993") — return as-is
	return dateString;
};

/**
 * Weekday utilities using Temporal API
 * Temporal uses ISO weekday numbering: 1=Monday, 2=Tuesday, ..., 7=Sunday
 */

export interface WeekdayOption {
	value: number;
	shortName: string;
	fullName: string;
	abbreviation: string;
}

export const WEEKDAYS: WeekdayOption[] = [
	{ value: 1, shortName: "Mon", fullName: "Monday", abbreviation: "M" },
	{ value: 2, shortName: "Tue", fullName: "Tuesday", abbreviation: "T" },
	{ value: 3, shortName: "Wed", fullName: "Wednesday", abbreviation: "W" },
	{ value: 4, shortName: "Thu", fullName: "Thursday", abbreviation: "T" },
	{ value: 5, shortName: "Fri", fullName: "Friday", abbreviation: "F" },
	{ value: 6, shortName: "Sat", fullName: "Saturday", abbreviation: "S" },
	{ value: 7, shortName: "Sun", fullName: "Sunday", abbreviation: "S" },
];

/**
 * Get weekday name from Temporal dayOfWeek value
 */
export const getWeekdayName = (
	dayOfWeek: number,
	format: "short" | "full" | "abbreviation" = "short",
): string => {
	const weekday = WEEKDAYS.find((w) => w.value === dayOfWeek);
	if (!weekday) throw new Error(`Invalid dayOfWeek: ${dayOfWeek}`);

	switch (format) {
		case "short":
			return weekday.shortName;
		case "full":
			return weekday.fullName;
		case "abbreviation":
			return weekday.abbreviation;
		default:
			return weekday.shortName;
	}
};

/**
 * Convert array of weekday numbers to display string
 * Example: [1, 3, 5] -> "Monday, Wednesday, Friday"
 */
export const formatWeekdaysDisplay = (
	weekdays: number[],
	format: "short" | "full" | "abbreviation" = "full",
	separator = ", ",
): string => {
	return weekdays
		.sort((a, b) => a - b)
		.map((day) => getWeekdayName(day, format))
		.join(separator);
};

/**
 * Convert array of weekday numbers to short string
 * Example: [1, 3, 5] -> "Mon,Wed,Fri"
 */
export const formatWeekdaysShort = (weekdays: number[]): string => {
	return weekdays
		.sort((a, b) => a - b)
		.map((day) => getWeekdayName(day, "short"))
		.join(",");
};

/**
 * Parse weekday string back to numbers
 * Example: "Mon,Wed,Fri" -> [1, 3, 5]
 */
export const parseWeekdaysShort = (weekdaysString: string): number[] => {
	if (!weekdaysString.trim()) return [];

	return weekdaysString
		.split(",")
		.map((day) => day.trim())
		.map((shortName) => {
			const weekday = WEEKDAYS.find((w) => w.shortName === shortName);
			if (!weekday) throw new Error(`Invalid weekday short name: ${shortName}`);
			return weekday.value;
		})
		.sort((a, b) => a - b);
};

/**
 * Get next occurrence of a specific weekday
 */
export const getNextWeekday = (
	targetWeekday: number,
	fromDate?: Temporal.PlainDate,
): Temporal.PlainDate => {
	const today = fromDate || Temporal.Now.plainDateISO();
	const currentWeekday = today.dayOfWeek;

	let daysToAdd = targetWeekday - currentWeekday;
	if (daysToAdd <= 0) {
		daysToAdd += 7; // Next week
	}

	return today.add({ days: daysToAdd });
};

/**
 * Check if a date falls on any of the specified weekdays
 */
export const isDateOnWeekdays = (
	date: Temporal.PlainDate,
	allowedWeekdays: number[],
): boolean => {
	return allowedWeekdays.includes(date.dayOfWeek);
};

/**
 * Generate common weekday combinations for CM Visit Schedule Options
 */
export const COMMON_VISIT_SCHEDULES = [
	{
		name: "Monday, Wednesday, Friday",
		weekdays: [1, 3, 5],
		shortCode: "Mon,Wed,Fri",
	},
	{
		name: "Tuesday, Thursday",
		weekdays: [2, 4],
		shortCode: "Tue,Thu",
	},
	{
		name: "Weekdays (Mon-Fri)",
		weekdays: [1, 2, 3, 4, 5],
		shortCode: "Mon,Tue,Wed,Thu,Fri",
	},
	{
		name: "Weekends (Sat-Sun)",
		weekdays: [6, 7],
		shortCode: "Sat,Sun",
	},
	{
		name: "Every Day",
		weekdays: [1, 2, 3, 4, 5, 6, 7],
		shortCode: "Mon,Tue,Wed,Thu,Fri,Sat,Sun",
	},
];

/**
 * Get ISO week number for a date
 */
export const getWeekNumber = (date: Temporal.PlainDate): number => {
	// Convert to ISO calendar if needed and get week of year
	const isoDate = date.withCalendar("iso8601");
	return isoDate.weekOfYear || 1;
};

/**
 * Get week schedule text for a date range
 */
export const getWeekScheduleText = (
	weekdays: number[],
	startDate: Temporal.PlainDate,
): string => {
	const dayNames = formatWeekdaysDisplay(weekdays, "short");
	const weekNumber = getWeekNumber(startDate);
	return `Week ${weekNumber}: ${dayNames}`;
};
